import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  Lock,
  Lightbulb,
  Target,
  TrendingUp,
  BookOpen,
  Zap,
  Users,
  DollarSign,
  Award,
  ClipboardList,
  Calendar,
  ChevronRight,
  X,
  Layers,
  Rocket
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

// ============================================
// CONTENT DATA - All slides in both languages
// ============================================
const SLIDES_CONTENT = {
  // SLIDE 1 - HOOK
  1: {
    type: 'content',
    en: {
      title: "How Your Income Really Works",
      text: [
        "Most people try to improve their finances by working more.",
        "But income does not grow that way.",
        "It grows when you increase the value you create."
      ]
    },
    es: {
      title: "Cómo Funciona Realmente Tu Ingreso",
      text: [
        "La mayoría intenta mejorar sus finanzas trabajando más.",
        "Pero el ingreso no crece así.",
        "Crece cuando aumentas el valor que creas."
      ]
    }
  },
  
  // SLIDE 2 - CORE MODEL
  2: {
    type: 'formula',
    en: {
      formula: "Income = Value × Scale × Capture",
      text: "Your income depends on three forces.",
      factors: [
        { name: "Value", desc: "how important your work is", icon: "Zap", color: "yellow" },
        { name: "Scale", desc: "how many people it impacts", icon: "Users", color: "blue" },
        { name: "Capture", desc: "how much of that value you keep", icon: "Target", color: "green" }
      ]
    },
    es: {
      formula: "Ingreso = Valor × Escala × Captura",
      text: "Tu ingreso depende de tres fuerzas.",
      factors: [
        { name: "Valor", desc: "qué tan importante es lo que haces", icon: "Zap", color: "yellow" },
        { name: "Escala", desc: "a cuántas personas impacta", icon: "Users", color: "blue" },
        { name: "Captura", desc: "cuánto de ese valor te quedas", icon: "Target", color: "green" }
      ]
    }
  },
  
  // SLIDE 3 - AHA MOMENT
  3: {
    type: 'content',
    en: {
      title: "The difference is not effort",
      text: [
        "Two people can work equally hard and earn very different incomes.",
        "The difference is how these three variables work together."
      ]
    },
    es: {
      title: "La diferencia no es el esfuerzo",
      text: [
        "Dos personas pueden trabajar igual de duro y ganar ingresos muy distintos.",
        "La diferencia está en cómo funcionan estas tres variables."
      ]
    }
  },
  
  // SLIDE 4 - TRANSITION TO VALUE
  4: {
    type: 'content',
    en: {
      title: "Let's focus on Value",
      text: [
        "Value is the foundation.",
        "If the value you create is low, everything else is limited."
      ]
    },
    es: {
      title: "Enfoquémonos en el Valor",
      text: [
        "El valor es la base.",
        "Si el valor que creas es bajo, todo lo demás se limita."
      ]
    }
  },
  
  // SLIDE 5 - VALUE EXPLANATION
  5: {
    type: 'educational',
    en: {
      title: "What Creating Value Really Means",
      paragraphs: [
        "Income does not increase simply because you work more hours.",
        "It increases when the value you produce becomes more important, more scarce, or harder to replace."
      ],
      subtitle: "Value grows when you:",
      points: [
        "solve important problems",
        "develop rare or difficult skills",
        "generate measurable results"
      ],
      closing: "The market rewards value that is hard to replace."
    },
    es: {
      title: "Qué Significa Realmente Crear Valor",
      paragraphs: [
        "El ingreso no aumenta solo porque trabajes más horas.",
        "Aumenta cuando el valor que produces se vuelve más importante, más escaso o más difícil de reemplazar."
      ],
      subtitle: "El valor crece cuando:",
      points: [
        "resuelves problemas importantes",
        "desarrollas habilidades raras o difíciles",
        "generas resultados medibles"
      ],
      closing: "El mercado recompensa el valor que es difícil de reemplazar."
    }
  },
  
  // SLIDE 6 - VALUE QUESTION (Multiple Choice)
  6: {
    type: 'multipleChoice',
    category: 'value',
    en: {
      title: "What Type of Value Do You Create?",
      subtitle: "Think about your main work or income source.",
      contextIntro: "The type of value you produce influences how scarce you are in the market.",
      contextDetail: "Some jobs are easily replaceable. Others multiply the impact of entire teams or systems.",
      question: "Which option best describes the type of work you primarily do?",
      options: [
        { id: 'operational', label: "Operational work", desc: "Routine tasks, manual labor, administration" },
        { id: 'specialized', label: "Specialized knowledge", desc: "Professional expertise, technical skills" },
        { id: 'sales', label: "Sales", desc: "Revenue generation, business development" },
        { id: 'management', label: "Management", desc: "Leading teams, making decisions" },
        { id: 'creation', label: "Creation of products or IP", desc: "Building products, content, inventions" },
        { id: 'capital', label: "Capital allocation", desc: "Investing, portfolio management" }
      ]
    },
    es: {
      title: "¿Qué Tipo de Valor Creas?",
      subtitle: "Piensa en tu trabajo principal o fuente de ingresos.",
      contextIntro: "El tipo de valor que produces influye en qué tan escaso eres en el mercado.",
      contextDetail: "Algunos trabajos son fácilmente reemplazables. Otros multiplican el impacto de equipos o sistemas completos.",
      question: "¿Cuál opción describe mejor el tipo de trabajo que realizas principalmente?",
      options: [
        { id: 'operational', label: "Trabajo operativo", desc: "Tareas rutinarias, trabajo manual, administración" },
        { id: 'specialized', label: "Conocimiento especializado", desc: "Experiencia profesional, habilidades técnicas" },
        { id: 'sales', label: "Ventas", desc: "Generación de ingresos, desarrollo de negocios" },
        { id: 'management', label: "Gestión", desc: "Liderar equipos, tomar decisiones" },
        { id: 'creation', label: "Creación de productos o PI", desc: "Construir productos, contenido, invenciones" },
        { id: 'capital', label: "Asignación de capital", desc: "Inversiones, gestión de portafolio" }
      ]
    }
  },
  
  // SLIDE 7 - VALUE INSIGHT
  7: {
    type: 'insight',
    category: 'value',
    en: {
      title: "Your Value Profile",
      insights: {
        operational: {
          level: "Entry Level",
          text: "Operational work is essential but highly replaceable. Many people can do it, which limits what the market will pay.",
          advice: "Consider developing specialized skills or certifications that make your work harder to replace."
        },
        specialized: {
          level: "Professional",
          text: "Specialized knowledge commands premium rates because fewer people have it. Your expertise is valuable.",
          advice: "Deepen your expertise in high-demand areas or combine multiple specializations for even greater value."
        },
        sales: {
          level: "Revenue Driver",
          text: "Sales directly impacts revenue, making it highly valued. Your ability to generate income is your greatest asset.",
          advice: "Focus on higher-value sales (enterprise, complex solutions) to increase your earning potential."
        },
        management: {
          level: "Multiplier",
          text: "Management multiplies the output of others. Your decisions affect entire teams and outcomes.",
          advice: "Develop strategic thinking and leadership skills to move into higher-impact roles."
        },
        creation: {
          level: "Builder",
          text: "Creating products or intellectual property has unlimited upside. What you build can generate value while you sleep.",
          advice: "Focus on creating assets that can scale — software, content, systems, or products."
        },
        capital: {
          level: "Allocator",
          text: "Capital allocation is the highest-leverage activity. You're making money work for you instead of trading time.",
          advice: "Continue developing your investment thesis and risk management skills."
        }
      },
      note: "Increasing your value usually means moving from replaceable work to irreplaceable expertise."
    },
    es: {
      title: "Tu Perfil de Valor",
      insights: {
        operational: {
          level: "Nivel Inicial",
          text: "El trabajo operativo es esencial pero altamente reemplazable. Muchas personas pueden hacerlo, lo que limita lo que el mercado pagará.",
          advice: "Considera desarrollar habilidades especializadas o certificaciones que hagan tu trabajo más difícil de reemplazar."
        },
        specialized: {
          level: "Profesional",
          text: "El conocimiento especializado exige tarifas premium porque menos personas lo tienen. Tu experiencia es valiosa.",
          advice: "Profundiza tu experiencia en áreas de alta demanda o combina múltiples especializaciones para mayor valor."
        },
        sales: {
          level: "Generador de Ingresos",
          text: "Las ventas impactan directamente los ingresos, haciéndolas muy valoradas. Tu capacidad para generar ingresos es tu mayor activo.",
          advice: "Enfócate en ventas de mayor valor (empresariales, soluciones complejas) para aumentar tu potencial de ganancias."
        },
        management: {
          level: "Multiplicador",
          text: "La gestión multiplica el resultado de otros. Tus decisiones afectan equipos enteros y resultados.",
          advice: "Desarrolla pensamiento estratégico y habilidades de liderazgo para moverte a roles de mayor impacto."
        },
        creation: {
          level: "Constructor",
          text: "Crear productos o propiedad intelectual tiene potencial ilimitado. Lo que construyes puede generar valor mientras duermes.",
          advice: "Enfócate en crear activos que puedan escalar — software, contenido, sistemas o productos."
        },
        capital: {
          level: "Asignador",
          text: "La asignación de capital es la actividad de mayor apalancamiento. Estás haciendo que el dinero trabaje para ti.",
          advice: "Continúa desarrollando tu tesis de inversión y habilidades de gestión de riesgo."
        }
      },
      note: "Aumentar tu valor generalmente significa pasar de trabajo reemplazable a experiencia irremplazable."
    }
  },
  
  // SLIDE 8 - REFLECTION INTRO
  8: {
    type: 'content',
    en: {
      title: "How Do You Create Value?",
      text: ["Take a moment to reflect on your current work."],
      isInstruction: true
    },
    es: {
      title: "¿Cómo Creas Valor?",
      text: ["Tómate un momento para reflexionar sobre tu trabajo actual."],
      isInstruction: true
    }
  },
  
  // SLIDE 9 - OPEN QUESTION 1
  9: {
    type: 'question',
    field: 'valueCreation',
    en: {
      question: "What do you do that creates value for others?",
      helper: "Examples: solving problems, managing teams, building products, selling solutions",
      placeholder: "Type your answer here..."
    },
    es: {
      question: "¿Qué haces que crea valor para otros?",
      helper: "Ejemplos: resolver problemas, liderar equipos, crear productos, vender soluciones",
      placeholder: "Escribe tu respuesta aquí..."
    }
  },
  
  // SLIDE 10 - OPEN QUESTION 2
  10: {
    type: 'question',
    field: 'stakeholders',
    en: {
      question: "Who benefits from the value you create?",
      helper: "Examples: boss, company, clients, users, investors",
      placeholder: "Type your answer here..."
    },
    es: {
      question: "¿Quién se beneficia del valor que creas?",
      helper: "Ejemplos: jefe, empresa, clientes, usuarios, inversionistas",
      placeholder: "Escribe tu respuesta aquí..."
    }
  },
  
  // SLIDE 11 - OPEN QUESTION 3
  11: {
    type: 'question',
    field: 'problemsSolved',
    en: {
      question: "What problems do you usually solve?",
      placeholder: "Type your answer here..."
    },
    es: {
      question: "¿Qué problemas resuelves normalmente?",
      placeholder: "Escribe tu respuesta aquí..."
    }
  },
  
  // SLIDE 12 - VALUE EXPANSION
  12: {
    type: 'question',
    field: 'valueExpansion',
    en: {
      question: "Where could you create more value?",
      placeholder: "Type your answer here..."
    },
    es: {
      question: "¿Dónde podrías crear más valor?",
      placeholder: "Escribe tu respuesta aquí..."
    }
  },
  
  // SLIDE 13 - SKILLS QUESTION
  13: {
    type: 'question',
    field: 'skillsToIncrease',
    en: {
      question: "What skills would increase your value?",
      placeholder: "Type your answer here..."
    },
    es: {
      question: "¿Qué habilidades aumentarían tu valor?",
      placeholder: "Escribe tu respuesta aquí..."
    }
  },
  
  // SLIDE 14 - LEARNING PLAN
  14: {
    type: 'question',
    field: 'learningPlan',
    en: {
      question: "What do you want to learn in the next 6–12 months?",
      placeholder: "Type your answer here..."
    },
    es: {
      question: "¿Qué quieres aprender en los próximos 6–12 meses?",
      placeholder: "Escribe tu respuesta aquí..."
    }
  },
  
  // SLIDE 15 - VALUE PROFILE (Generated)
  15: {
    type: 'profile',
    en: {
      title: "Your Value Creation Profile",
      labels: {
        valueCreation: "You currently create value by:",
        stakeholders: "Your main stakeholders:",
        valueExpansion: "Opportunities to increase value:",
        skillsToIncrease: "Skills to develop:"
      }
    },
    es: {
      title: "Tu Perfil de Creación de Valor",
      labels: {
        valueCreation: "Actualmente creas valor:",
        stakeholders: "Tus principales beneficiarios:",
        valueExpansion: "Oportunidades para aumentar valor:",
        skillsToIncrease: "Habilidades a desarrollar:"
      }
    }
  },
  
  // SLIDE 16 - VALUE GAP
  16: {
    type: 'gap',
    en: {
      title: "Your Value Gap",
      text: "You may be capable of creating significantly more value than you currently do.",
      levels: {
        low: "Low Gap",
        medium: "Medium Gap",
        high: "High Gap"
      }
    },
    es: {
      title: "Tu Brecha de Valor",
      text: "Es posible que seas capaz de crear significativamente más valor del que creas actualmente.",
      levels: {
        low: "Brecha Baja",
        medium: "Brecha Media",
        high: "Brecha Alta"
      }
    }
  },
  
  // SLIDE 17 - PLAN PREVIEW (FREE)
  17: {
    type: 'preview',
    en: {
      title: "Your Value Creation Plan",
      steps: [
        { num: 1, text: "Develop new high-value skills" },
        { num: 2, text: "Apply them to bigger problems" },
        { num: 3, text: "Increase your impact" }
      ],
      footer: "This is your path to increasing your income."
    },
    es: {
      title: "Tu Plan de Creación de Valor",
      steps: [
        { num: 1, text: "Desarrollar nuevas habilidades de alto valor" },
        { num: 2, text: "Aplicarlas a problemas más grandes" },
        { num: 3, text: "Aumentar tu impacto" }
      ],
      footer: "Este es tu camino para aumentar tu ingreso."
    }
  },
  
  // SLIDE 18 - PREMIUM GATE
  18: {
    type: 'gate',
    en: {
      title: "Turn Insight into Action",
      text: "Unlock your personalized action plan and start building more value today.",
      button: "Unlock My Plan"
    },
    es: {
      title: "Convierte el Conocimiento en Acción",
      text: "Desbloquea tu plan de acción personalizado y comienza a crear más valor hoy.",
      button: "Desbloquear Mi Plan"
    }
  },
  
  // SLIDE 19 - ACTION PLAN (PREMIUM)
  19: {
    type: 'actionPlan',
    premium: true,
    en: {
      title: "Your Personalized Action Plan",
      generating: "Generating your action plan...",
      columns: {
        action: "Action",
        description: "Description",
        timeline: "Timeline",
        dependencies: "Dependencies"
      }
    },
    es: {
      title: "Tu Plan de Acción Personalizado",
      generating: "Generando tu plan de acción...",
      columns: {
        action: "Acción",
        description: "Descripción",
        timeline: "Cronograma",
        dependencies: "Dependencias"
      }
    }
  }
};

const TOTAL_SLIDES = 19;
const FREE_SLIDES = 18;

// Page transition variants
const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -50, transition: { duration: 0.2 } }
};

// ============================================
// MAIN COMPONENT
// ============================================
const ValueCreationModuleV2 = ({ onClose, userProfile, isPremium = false }) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [answers, setAnswers] = useState({
    value: null,
    scale: null,
    capture: null,
    valueCreation: '',
    stakeholders: '',
    problemsSolved: '',
    valueExpansion: '',
    skillsToIncrease: '',
    learningPlan: ''
  });
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [valueGap, setValueGap] = useState('medium');
  
  const textareaRef = useRef(null);
  
  // Determine language from user profile
  const lang = userProfile?.language === 'es' ? 'es' : 'en';
  
  // Get current slide content
  const slideData = SLIDES_CONTENT[currentSlide];
  const content = slideData?.[lang] || slideData?.en;
  
  // Calculate progress
  const maxSlide = isPremium ? TOTAL_SLIDES : FREE_SLIDES;
  const progress = (currentSlide / maxSlide) * 100;
  
  // Focus textarea when question slide appears
  useEffect(() => {
    if (slideData?.type === 'question' && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [currentSlide, slideData?.type]);
  
  // Calculate value gap based on answers
  useEffect(() => {
    if (currentSlide === 16) {
      const filledAnswers = Object.values(answers).filter(a => a && a.length > 20).length;
      const expansionLength = answers.valueExpansion?.length || 0;
      const skillsLength = answers.skillsToIncrease?.length || 0;
      
      if (expansionLength > 50 && skillsLength > 50 && filledAnswers >= 5) {
        setValueGap('high');
      } else if (expansionLength > 30 || skillsLength > 30) {
        setValueGap('medium');
      } else {
        setValueGap('low');
      }
    }
  }, [currentSlide, answers]);
  
  // Navigation handlers
  const canGoNext = () => {
    if (slideData?.type === 'question') {
      const fieldValue = answers[slideData.field] || '';
      return fieldValue.trim().length >= 3;
    }
    if (slideData?.type === 'multipleChoice') {
      return answers[slideData.category] !== null;
    }
    if (slideData?.type === 'gate' && !isPremium) {
      return false;
    }
    return true;
  };
  
  const goNext = () => {
    if (currentSlide < maxSlide && canGoNext()) {
      setCurrentSlide(prev => prev + 1);
    } else if (currentSlide === maxSlide) {
      // On last slide, close the module
      onClose?.();
    }
  };
  
  const goBack = () => {
    if (currentSlide > 1) {
      setCurrentSlide(prev => prev - 1);
    }
  };
  
  const handleAnswerChange = (value) => {
    if (slideData?.field) {
      setAnswers(prev => ({
        ...prev,
        [slideData.field]: value
      }));
    }
  };
  
  const handleMultipleChoiceAnswer = (category, optionId) => {
    setAnswers(prev => ({ ...prev, [category]: optionId }));
    // Auto-advance after selection - use functional update to ensure we have latest state
    setTimeout(() => {
      setCurrentSlide(prev => prev < maxSlide ? prev + 1 : prev);
    }, 400);
  };
  
  // Generate action plan (premium feature)
  const generateActionPlan = async () => {
    setIsGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const plan = [
      {
        action: lang === 'es' ? 'Identificar habilidad clave' : 'Identify key skill',
        description: lang === 'es' 
          ? `Basado en tu respuesta: "${answers.skillsToIncrease?.substring(0, 50)}..."` 
          : `Based on your answer: "${answers.skillsToIncrease?.substring(0, 50)}..."`,
        timeline: lang === 'es' ? '2 semanas' : '2 weeks',
        dependencies: lang === 'es' ? 'Ninguna' : 'None'
      },
      {
        action: lang === 'es' ? 'Crear plan de aprendizaje' : 'Create learning plan',
        description: lang === 'es' 
          ? 'Estructurar recursos y tiempo de estudio' 
          : 'Structure resources and study time',
        timeline: lang === 'es' ? '1 semana' : '1 week',
        dependencies: lang === 'es' ? 'Paso 1' : 'Step 1'
      },
      {
        action: lang === 'es' ? 'Aplicar conocimiento' : 'Apply knowledge',
        description: lang === 'es' 
          ? `En el área: "${answers.valueExpansion?.substring(0, 40)}..."` 
          : `In the area: "${answers.valueExpansion?.substring(0, 40)}..."`,
        timeline: lang === 'es' ? '4-8 semanas' : '4-8 weeks',
        dependencies: lang === 'es' ? 'Paso 2' : 'Step 2'
      },
      {
        action: lang === 'es' ? 'Medir impacto' : 'Measure impact',
        description: lang === 'es' 
          ? 'Evaluar resultados con stakeholders' 
          : 'Evaluate results with stakeholders',
        timeline: lang === 'es' ? '2 semanas' : '2 weeks',
        dependencies: lang === 'es' ? 'Paso 3' : 'Step 3'
      }
    ];
    
    setGeneratedPlan(plan);
    setIsGenerating(false);
  };
  
  // Auto-generate content for premium slides
  useEffect(() => {
    if (currentSlide === 19 && isPremium && !generatedPlan) {
      generateActionPlan();
    }
  }, [currentSlide, isPremium, generatedPlan]);
  
  // ============================================
  // RENDER FUNCTIONS - DARK THEME
  // ============================================
  
  // Content slide (educational)
  const renderContentSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[50vh] px-5 py-6 text-center"
    >
      <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-5 leading-tight max-w-lg">
        {content.title}
      </h1>
      
      <div className="space-y-3 max-w-md">
        {content.text?.map((paragraph, idx) => (
          <motion.p
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 + idx * 0.15 }}
            className={`text-base md:text-lg leading-relaxed ${
              content.isInstruction 
                ? 'text-yellow-400 italic' 
                : 'text-gray-300'
            }`}
          >
            {paragraph}
          </motion.p>
        ))}
      </div>
    </motion.div>
  );
  
  // Educational slide with points
  const renderEducationalSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      <h1 className="text-xl md:text-2xl font-bold text-white mb-4">{content.title}</h1>
      
      <div className="space-y-3 mb-4 max-w-md">
        {content.paragraphs?.map((para, i) => (
          <p key={i} className="text-gray-300 text-sm leading-relaxed">{para}</p>
        ))}
      </div>
      
      <p className="text-yellow-400 text-sm font-medium mb-3">{content.subtitle}</p>
      
      <div className="space-y-2 mb-4 max-w-sm">
        {content.points?.map((point, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></div>
            <p className="text-gray-300 text-sm text-left">{point}</p>
          </div>
        ))}
      </div>
      
      {content.closing && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 max-w-sm">
          <p className="text-yellow-400 text-sm font-medium">{content.closing}</p>
        </div>
      )}
    </motion.div>
  );
  
  // Formula slide
  const renderFormulaSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      {/* Formula */}
      <div className="bg-gradient-to-r from-yellow-500 to-amber-600 text-black rounded-xl px-6 py-4 mb-6 shadow-lg">
        <p className="text-xl md:text-2xl font-bold tracking-wide">
          {content.formula}
        </p>
      </div>
      
      <p className="text-lg text-gray-300 mb-6">{content.text}</p>
      
      {/* Factors */}
      <div className="space-y-3 w-full max-w-sm">
        {content.factors?.map((factor, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + idx * 0.12 }}
            className="flex items-center gap-4 bg-gray-800/50 border border-gray-700 rounded-xl p-3 text-left"
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              factor.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-500' :
              factor.color === 'blue' ? 'bg-blue-500/20 text-blue-500' :
              'bg-green-500/20 text-green-500'
            }`}>
              {factor.icon === 'Zap' && <Zap className="w-5 h-5" />}
              {factor.icon === 'Users' && <Users className="w-5 h-5" />}
              {factor.icon === 'Target' && <Target className="w-5 h-5" />}
            </div>
            <div>
              <span className="font-semibold text-white">{factor.name}</span>
              <span className="text-gray-400"> — {factor.desc}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
  
  // Multiple choice question slide
  const renderMultipleChoiceSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full py-2"
    >
      {/* Header - Compact for mobile */}
      <div className="flex flex-col items-center flex-shrink-0 px-4 mb-2">
        <div className="w-8 h-8 rounded-lg bg-yellow-500/20 text-yellow-500 flex items-center justify-center mb-1">
          <Zap className="w-4 h-4" />
        </div>
        
        <h1 className="text-base md:text-lg font-bold text-white mb-1 text-center">{content.title}</h1>
        
        {content.contextIntro && (
          <div className="bg-gray-800/30 rounded-lg p-2 mb-2 max-w-md">
            <p className="text-gray-400 text-xs text-center leading-snug">{content.contextIntro}</p>
          </div>
        )}
        
        <p className="text-gray-300 text-xs text-center max-w-md">{content.question}</p>
      </div>
      
      {/* Options - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="grid gap-2 w-full max-w-md mx-auto">
          {content.options?.map((option) => (
            <button
              key={option.id}
              onClick={() => handleMultipleChoiceAnswer(slideData.category, option.id)}
              className={`bg-gray-800/50 border hover:border-yellow-500/50 hover:bg-gray-800 rounded-lg p-2.5 text-left transition-all group ${
                answers[slideData.category] === option.id 
                  ? 'border-yellow-500 bg-gray-800' 
                  : 'border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className={`font-medium text-sm transition-colors ${
                    answers[slideData.category] === option.id 
                      ? 'text-yellow-400' 
                      : 'text-white group-hover:text-yellow-400'
                  }`}>{option.label}</p>
                  {option.desc && <p className="text-gray-500 text-xs mt-0.5 leading-tight">{option.desc}</p>}
                </div>
                <ChevronRight className={`w-4 h-4 flex-shrink-0 ml-2 transition-colors ${
                  answers[slideData.category] === option.id 
                    ? 'text-yellow-400' 
                    : 'text-gray-600 group-hover:text-yellow-400'
                }`} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
  
  // Insight slide (based on multiple choice answer)
  const renderInsightSlide = () => {
    const insight = content.insights?.[answers[slideData.category]];
    if (!insight) {
      // Default if no answer selected
      return renderContentSlide();
    }
    
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col items-center justify-center min-h-[60vh] px-4"
      >
        <div className="w-12 h-12 rounded-lg bg-yellow-500/20 text-yellow-500 flex items-center justify-center mb-3">
          <Zap className="w-6 h-6" />
        </div>
        
        <h1 className="text-xl md:text-2xl font-bold text-white mb-4 text-center">{content.title}</h1>
        
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 max-w-md w-full mb-4">
          <div className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 bg-yellow-500/20 text-yellow-400">
            {insight.level}
          </div>
          
          <p className="text-gray-300 text-sm mb-3 leading-relaxed">{insight.text}</p>
          
          {insight.advice && (
            <div className="bg-gray-900/50 rounded-lg p-3 border-l-2 border-yellow-500">
              <p className="text-gray-400 text-xs">{insight.advice}</p>
            </div>
          )}
        </div>
        
        {content.note && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 max-w-md">
            <p className="text-yellow-400 text-xs">💡 {content.note}</p>
          </div>
        )}
      </motion.div>
    );
  };
  
  // Question slide (open text)
  const renderQuestionSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[50vh] px-5 py-6"
    >
      <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-white mb-3 text-center max-w-md leading-snug">
        {content.question}
      </h2>
      
      {content.helper && (
        <p className="text-gray-500 text-xs md:text-sm mb-4 text-center max-w-sm">
          {content.helper}
        </p>
      )}
      
      <textarea
        ref={textareaRef}
        value={answers[slideData.field] || ''}
        onChange={(e) => handleAnswerChange(e.target.value)}
        placeholder={content.placeholder}
        className="w-full max-w-md min-h-[120px] text-base p-4 rounded-xl bg-gray-800/50 border border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 text-white placeholder-gray-500 resize-none"
      />
      
      <p className="text-xs text-gray-500 mt-2">
        {(answers[slideData.field] || '').length} {lang === 'es' ? 'caracteres' : 'characters'}
      </p>
    </motion.div>
  );
  
  // Profile slide (generated summary)
  const renderProfileSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-start min-h-[60vh] px-4 py-6"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-yellow-500/20 text-yellow-500 flex items-center justify-center">
          <Award className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-bold text-white">{content.title}</h2>
      </div>
      
      <div className="space-y-3 w-full max-w-md">
        {Object.entries(content.labels).map(([key, label]) => (
          <div key={key} className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <p className="text-xs font-medium text-yellow-400 mb-1">{label}</p>
            <p className="text-gray-300 text-sm">
              {answers[key] || (lang === 'es' ? 'No respondido' : 'Not answered')}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
  
  // Gap slide
  const renderGapSlide = () => {
    const gapColors = {
      low: 'from-green-400 to-green-500',
      medium: 'from-yellow-400 to-amber-500',
      high: 'from-orange-500 to-red-500'
    };
    
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center"
      >
        <h2 className="text-2xl font-bold text-white mb-4">{content.title}</h2>
        
        <p className="text-gray-300 mb-6 max-w-md">{content.text}</p>
        
        {/* Gap indicator */}
        <div className="w-full max-w-xs h-3 bg-gray-700 rounded-full overflow-hidden mb-4">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: valueGap === 'low' ? '33%' : valueGap === 'medium' ? '66%' : '100%' }}
            transition={{ duration: 1, delay: 0.3 }}
            className={`h-full bg-gradient-to-r ${gapColors[valueGap]} rounded-full`}
          />
        </div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className={`inline-block px-5 py-2 rounded-full text-black font-semibold bg-gradient-to-r ${gapColors[valueGap]}`}
        >
          {content.levels[valueGap]}
        </motion.div>
      </motion.div>
    );
  };
  
  // Preview slide
  const renderPreviewSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center"
    >
      <h2 className="text-2xl font-bold text-white mb-6">{content.title}</h2>
      
      <div className="space-y-3 mb-6 w-full max-w-sm">
        {content.steps?.map((step, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + idx * 0.15 }}
            className="flex items-center gap-4 bg-gray-800/50 border border-gray-700 rounded-xl p-3 text-left"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 text-black flex items-center justify-center font-bold flex-shrink-0">
              {step.num}
            </div>
            <p className="text-gray-200 text-sm font-medium">{step.text}</p>
          </motion.div>
        ))}
      </div>
      
      <p className="text-yellow-400 font-medium text-sm">{content.footer}</p>
    </motion.div>
  );
  
  // Gate slide (premium upsell)
  const renderGateSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center"
    >
      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 flex items-center justify-center mx-auto mb-5">
        <Lock className="w-8 h-8 text-black" />
      </div>
      
      <h2 className="text-2xl font-bold text-white mb-3">{content.title}</h2>
      
      <p className="text-gray-300 mb-6 max-w-sm">{content.text}</p>
      
      {!isPremium ? (
        <button
          onClick={() => toast.info(lang === 'es' ? 'Función premium próximamente' : 'Premium feature coming soon')}
          className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2"
        >
          <Sparkles className="w-5 h-5" />
          {content.button}
        </button>
      ) : (
        <button
          onClick={goNext}
          className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2"
        >
          {lang === 'es' ? 'Continuar' : 'Continue'}
          <ArrowRight className="w-5 h-5" />
        </button>
      )}
    </motion.div>
  );
  
  // Action plan slide (premium)
  const renderActionPlanSlide = () => (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-start min-h-[60vh] px-4 py-6"
    >
      <h2 className="text-xl font-bold text-white mb-5 text-center">{content.title}</h2>
      
      {isGenerating ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-3 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mb-4" />
          <p className="text-gray-400 text-sm">{content.generating}</p>
        </div>
      ) : (
        <div className="space-y-3 w-full max-w-md">
          {generatedPlan?.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-gray-800/50 border border-gray-700 rounded-xl p-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="font-bold text-xs">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white text-sm">{item.action}</h3>
                  <p className="text-gray-400 text-xs mt-1">{item.description}</p>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span><Calendar className="w-3 h-3 inline mr-1" />{item.timeline}</span>
                    <span><ClipboardList className="w-3 h-3 inline mr-1" />{item.dependencies}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
  
  // Render current slide based on type
  const renderSlide = () => {
    switch (slideData?.type) {
      case 'content':
        return renderContentSlide();
      case 'educational':
        return renderEducationalSlide();
      case 'formula':
        return renderFormulaSlide();
      case 'multipleChoice':
        return renderMultipleChoiceSlide();
      case 'insight':
        return renderInsightSlide();
      case 'question':
        return renderQuestionSlide();
      case 'profile':
        return renderProfileSlide();
      case 'gap':
        return renderGapSlide();
      case 'preview':
        return renderPreviewSlide();
      case 'gate':
        return renderGateSlide();
      case 'actionPlan':
        return renderActionPlanSlide();
      default:
        return renderContentSlide();
    }
  };
  
  // ============================================
  // MAIN RENDER - DARK THEME
  // ============================================
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-black z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
          
          {/* Progress bar */}
          <div className="flex-1 mx-4 max-w-xs">
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="h-full bg-gradient-to-r from-yellow-500 to-amber-600 rounded-full"
              />
            </div>
            <p className="text-xs text-gray-500 text-center mt-1">
              {currentSlide} / {maxSlide}
            </p>
          </div>
          
          <div className="w-9" />
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto h-full">
          <AnimatePresence mode="wait">
            {renderSlide()}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Navigation - Fixed at bottom with safe area padding */}
      <div className="flex-shrink-0 bg-gray-900/95 backdrop-blur-sm border-t border-gray-800">
        <div className="max-w-2xl mx-auto px-4 py-3 pb-8 flex items-center justify-between">
          <button
            onClick={goBack}
            disabled={currentSlide === 1}
            className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'es' ? 'Atrás' : 'Back'}
          </button>
          
          {slideData?.type !== 'gate' || isPremium ? (
            slideData?.type !== 'multipleChoice' && (
              <button
                onClick={goNext}
                disabled={!canGoNext() && currentSlide < maxSlide}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                {currentSlide === maxSlide 
                  ? (lang === 'es' ? 'Finalizar' : 'Finish')
                  : (lang === 'es' ? 'Continuar' : 'Continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ValueCreationModuleV2;
